<template>
	<v-img :src="src" :lazy-src="undefined" :aspect-ratio="aspectRatio">
		<template #placeholder>
			<slot name="placeholder" v-bind="{ centerClasses }">
				<Loading />
			</slot>
		</template>
		<template #error>
			<slot
				name="error"
				v-bind="{
					errorIcon: translate(errorIcon),
					errorText: translate(errorText),
					centerClasses,
				}"
			>
				<div :class="centerClasses">
					<v-icon
						:size="errorIconSize ?? undefined"
						:icon="translate(errorIcon)"
					></v-icon>
					<span v-if="showFileNotFoundText" class="my-5">
						{{ translate(errorText) }}
						<v-tooltip
							v-if="errorInfo"
							:text="translate(errorInfo)"
							location="bottom"
						>
							<template #activator="{ props }">
								<v-icon v-bind="props" icon="far fa-circle-info"></v-icon>
							</template>
						</v-tooltip>
					</span>
					<ButtonIcon
						v-if="reloadBtn"
						type="reload"
						variant="tonal"
						@click="$emit('reload')"
					></ButtonIcon>
				</div>
			</slot>
		</template>
	</v-img>
</template>

<script setup lang="ts">
import { useTranslate } from '@/composables/useTranslate'
import type { srcObject } from 'vuetify/lib/components/VImg/VImg.mjs'
import Loading from './Loading.vue'

export type ImageSrc = string | srcObject | undefined | null

const centerClasses =
	'd-flex flex-column justify-center align-center fill-height'

type BaseImageProps = {
	aspectRatio?: string
	reloadBtn?: boolean
	showFileNotFoundText?: boolean
	errorIcon?: string
	errorText?: string
	errorInfo?: string
	errorIconSize?: string | null
}

const {
	aspectRatio = '1',
	errorIconSize = 'x-large',
	errorIcon = 'component.baseImage.errorIcon',
	errorText = 'component.baseImage.error',
	errorInfo = undefined,
} = defineProps<BaseImageProps>()
const { translate } = useTranslate()

const src = defineModel<ImageSrc>('src', {
	required: true,
	get(value) {
		if (value === null) return 'null'
		return value
	},
	set(value) {
		if (value === 'null') return null
		return value
	},
})

defineEmits(['reload'])
</script>
